// ============================================================================
// conditionalFormat.ts — движок условного оформления (как в 1С, на стилях).
// Правила — SSoT сервера (metadata_conditional_formats); вычисление — на
// клиенте (как в 1С). Условия: по значениям полей + контекст пользователя
// (__current_user__, __current_roles__, __is_admin__). Оформление: стиль
// (metadata_styles) + hidden/disabled. Применяется к элементам форм
// (field:<code>) и строкам/колонкам списков (row, column:<code>).
// ============================================================================

export interface CondCondition {
  field: string;
  operator: string; // = | != | > | < | >= | <= | is_null | is_not_null | like | in
  value: string;
  logic: string; // and | or
  group: number;
}

export interface CondFormat {
  id: string;
  scope: string; // form | list
  target: string; // catalog:code | document:code | form:<id>
  appliesTo: string[]; // field:<code> | row | column:<code>
  conditions: CondCondition[];
  styleId: string;
  hidden: boolean;
  disabled: boolean;
  sortOrder: number;
  isActive: boolean;
  description: string;
}

export interface CondContext {
  currentUserId: string;
  currentUserRoles: string[];
  isAdmin: boolean;
}

// Вычисление одного условия.
function evalCondition(c: CondCondition, data: Record<string, unknown>, ctx: CondContext): boolean {
  const rawValue = data[c.field];
  let actual: string;
  switch (c.field) {
    case '__current_user__':
    case '__current_user_id__':
      actual = ctx.currentUserId;
      break;
    case '__is_admin__':
      actual = ctx.isAdmin ? 'true' : 'false';
      break;
    case '__current_roles__':
      actual = (ctx.currentUserRoles || []).join(',');
      break;
    default:
      actual = rawValue === undefined || rawValue === null ? '' : String(rawValue);
  }
  const expected = c.value || '';
  switch (c.operator) {
    case 'is_null': return actual === '';
    case 'is_not_null': return actual !== '';
    case 'like': return actual.toLowerCase().includes(expected.toLowerCase());
    case 'in': {
      if (c.field === '__current_roles__') {
        return expected.split(',').map((s) => s.trim()).some((r) => (ctx.currentUserRoles || []).includes(r));
      }
      return expected.split(',').map((s) => s.trim()).includes(actual);
    }
    case '>': return Number(actual) > Number(expected);
    case '<': return Number(actual) < Number(expected);
    case '>=': return Number(actual) >= Number(expected);
    case '<=': return Number(actual) <= Number(expected);
    case '!=': return actual !== expected;
    default: return actual === expected;
  }
}

// Вычисление правила: условия группируются по group (разные группы = OR,
// внутри группы = AND с учётом logic).
export function evalConditions(conditions: CondCondition[], data: Record<string, unknown>, ctx: CondContext): boolean {
  if (!conditions || conditions.length === 0) return false;
  const groups = new Map<number, CondCondition[]>();
  for (const c of conditions) {
    const g = c.group || 0;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(c);
  }
  const groupResults: boolean[] = [];
  for (const list of groups.values()) {
    let result = evalCondition(list[0], data, ctx);
    for (let i = 1; i < list.length; i++) {
      const c = list[i];
      const v = evalCondition(c, data, ctx);
      result = c.logic === 'or' ? (result || v) : (result && v);
    }
    groupResults.push(result);
  }
  return groupResults.some(Boolean);
}

// Применённое оформление для элемента/строки.
export interface AppliedFormat {
  styleId?: string;
  hidden: boolean;
  disabled: boolean;
}

// Вычисление оформления для объекта (данные) по правилам scope+target.
export function applyFormats(rules: CondFormat[], data: Record<string, unknown>, ctx: CondContext, appliesTo?: string): AppliedFormat {
  const result: AppliedFormat = { hidden: false, disabled: false };
  for (const rule of rules) {
    if (!rule.isActive) continue;
    if (appliesTo && !rule.appliesTo.includes(appliesTo)) continue;
    if (!evalConditions(rule.conditions, data, ctx)) continue;
    if (rule.styleId) result.styleId = rule.styleId;
    if (rule.hidden) result.hidden = true;
    if (rule.disabled) result.disabled = true;
  }
  return result;
}
